import { useState, useCallback } from 'react';

const HeroSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);

  const images = [
    'https://static.readdy.ai/image/352cce8cf532226003c93d0e98b623eb/4e561d202a160243d27dc8f66fd52ab6.jpeg',
    'https://static.readdy.ai/image/352cce8cf532226003c93d0e98b623eb/197aa4670cd8c77f3ec17bbe1e8854d0.jpeg',
    'https://static.readdy.ai/image/352cce8cf532226003c93d0e98b623eb/7ed0e934e77035ad079182004f74e38f.jpeg',
    'https://static.readdy.ai/image/352cce8cf532226003c93d0e98b623eb/70f7d40d4a8e4f27cc08013bc82afbdc.jpeg',
    'https://static.readdy.ai/image/352cce8cf532226003c93d0e98b623eb/21c7012ea0bed5b86fd2cb4aabb86ee4.jpeg',
    'https://static.readdy.ai/image/352cce8cf532226003c93d0e98b623eb/f0bdb7e49427aa74e1ee0640c1ed5868.jpeg',
    'https://static.readdy.ai/image/352cce8cf532226003c93d0e98b623eb/a29f76ab16fb8840d965a9c9373c4d56.jpeg',
    'https://static.readdy.ai/image/352cce8cf532226003c93d0e98b623eb/b777b0aa0a37a772e2817a6d85dd567b.jpeg',
    'https://static.readdy.ai/image/352cce8cf532226003c93d0e98b623eb/118d87f6a15435f82e057ba4070c85f7.jpeg'
  ];

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const openGallery = useCallback(() => {
    setShowGallery(true);
  }, []);

  const closeGallery = useCallback(() => {
    setShowGallery(false);
  }, []);

  return (
    <>
      {/* Full Screen Image Section */}
      <section className="relative w-full h-screen overflow-hidden bg-black">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={images[currentImageIndex]}
            alt={`Lexi Geraghty - Photo ${currentImageIndex + 1}`}
            className="w-full h-full object-cover object-center"
            style={{
              filter: 'contrast(1.15) saturate(1.2) brightness(1.05) hue-rotate(2deg)'
            }}
            loading="eager"
          />
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        {/* Navigation Controls */}
        <button
          onClick={prevImage}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-gray-500/80 text-white w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 z-20"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        
        <button
          onClick={nextImage}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-gray-500/80 text-white w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 z-20"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>

        {/* Photo Counter */}
        <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm z-20 font-medium">
          {currentImageIndex + 1} of {images.length}
        </div>

        {/* Tap to expand hint */}
        <button
          onClick={openGallery}
          className="absolute top-6 right-6 bg-black/60 backdrop-blur-sm text-white px-3 py-2 rounded-full text-xs flex items-center gap-2 hover:bg-black/80 transition-all z-20"
        >
          <i className="ri-expand-diagonal-line"></i>
          Gallery
        </button>

        {/* Enhanced Scroll Down Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center z-20">
          <div className="animate-bounce mb-3">
            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 mb-2">
              <i className="ri-arrow-down-line text-2xl text-white"></i>
            </div>
          </div>
          <p className="text-white text-sm font-semibold drop-shadow-lg bg-black/40 px-3 py-1 rounded-full">
            Scroll to learn more
          </p>
        </div>
      </section>

      {/* Text Content Section - Appears when scrolling down */}
      <section className="bg-gradient-to-b from-white to-pink-50 px-4 py-16 min-h-screen">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Lexi Geraghty is Looking for Someone Special 🔥
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed max-w-3xl mx-auto">
            Fort Lauderdale's Chief of Staff has built a multi-million dollar empire and knows exactly what she wants in a partner
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <a
              href="#application"
              className="bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap"
            >
              Apply to Take Lexi Out
            </a>
            
            <a
              href="#meet-lexi"
              className="border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap"
            >
              Learn About Lexi
            </a>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 border-t border-gray-200">
            <div className="text-center">
              <div className="text-3xl font-bold text-pink-600 mb-2">247</div>
              <div className="text-gray-600">Applications Received</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-pink-600 mb-2">Chief of Staff</div>
              <div className="text-gray-600">Fort Lauderdale</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-pink-600 mb-2">Multi-Million</div>
              <div className="text-gray-600">Dollar Empire</div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={closeGallery}
              className="absolute -top-12 right-0 text-white hover:text-yellow-400 text-2xl z-10"
            >
              <i className="ri-close-line"></i>
            </button>
            
            <div className="relative">
              <img
                src={images[currentImageIndex]}
                alt={`Lexi Geraghty - Photo ${currentImageIndex + 1}`}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                style={{
                  filter: 'contrast(1.15) saturate(1.2) brightness(1.05) hue-rotate(2deg)'
                }}
              />
              
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-yellow-500/80 text-white w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-yellow-500/80 text-white w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300"
              >
                <i className="ri-arrow-right-line text-xl"></i>
              </button>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full">
                {currentImageIndex + 1} of {images.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSection;
